/**
 * The Database import flow without the card around it (the staff list: the only list still imported by hand -
 * every Ejar table comes from the engine's hourly sync, owner 28 Sep 2026).
 *
 *   previewImport({ importer, mode, filesById })             -> { plan, rowCount, writes, ... }
 *   applyImport({ importer, mode, filesById, plan, confirm }) -> { plan, counts, entryId, skipped, ... }
 *
 * `confirm(question, { plan, writes })` is what gates a destructive apply: the
 * card resolves it with its in-card confirmation panel. `skipped` is true when
 * the file held nothing to write (the check was recorded instead) or when
 * `confirm` said no (`cancelled` is set, `question` holds what was asked).
 */
import { importLogService } from './importLogService';
import { importUndoService } from './importUndoService';
import { adminService } from './adminService';
import logger from '../utils/logger';

const writesOfCounts = (c = {}) => (c.add || 0) + (c.update || 0) + (c.close || 0) + (c.enrich || 0) + (c.chunks || 0) + (c.deleteChunks || 0) + (c.remove || 0) + (c.statusMap || 0);

/** Number of writes a plan would make: the sum of its sections when it has some, else its own counts. */
export const writesOf = (plan) => {
    if (!plan) return 0;
    if (Array.isArray(plan.sections)) return plan.sections.reduce((sum, s) => sum + writesOfCounts(s.counts), 0);
    return writesOfCounts(plan.counts);
};

/** File slots of an importer: `files: [{ id, label, accept, required }]`, or one slot built from `accept`. */
export const fileSlotsOf = (importer) => (Array.isArray(importer.files) && importer.files.length > 0
    ? importer.files.map(f => ({ required: false, ...f }))
    : [{ id: 'file', label: 'File', accept: importer.accept || '.xlsx,.xls', required: true }]);

/** Rows of a single-file parse (an array) or of a multi-file one (an object of arrays). */
export const rowCountOf = (rows) => (Array.isArray(rows)
    ? rows.length
    : Object.values(rows || {}).reduce((sum, r) => sum + (Array.isArray(r) ? r.length : 0), 0));

/** Chosen files of an importer: what to parse, the joined file name and the per-slot names. */
export const fileInfoOf = (importer, filesById = {}) => {
    const slots = fileSlotsOf(importer);
    const chosen = slots.filter(s => filesById[s.id]);
    return {
        slots,
        chosen,
        ready: chosen.length > 0 && slots.every(s => !s.required || filesById[s.id]),
        files: Object.fromEntries(chosen.map(s => [s.id, filesById[s.id]])),
        fileName: chosen.map(s => filesById[s.id].name).join(' + ') || undefined,
        fileNames: Object.fromEntries(chosen.map(s => [s.id, filesById[s.id].name]))
    };
};

/**
 * Records a check that found nothing to write: the table counts as updated now
 * (zero counts, mode, file name), so the freshness badge turns green without
 * another click. Combined importers also refresh their section logs.
 */
const recordCheck = async (importer, plan, { mode, fileName, fileNames }) => {
    await importLogService.record({ table: importer.table, mode, fileName, counts: plan.counts });
    if (typeof importer.recordCheck === 'function') await importer.recordCheck(plan, { mode, fileName, fileNames });
};

/**
 * The question an apply has to ask first: a "danger" mode wipes the table, and
 * a plan that removes / archives existing data carries its own `warning`.
 * Returns null when the apply can just run.
 */
export const confirmQuestion = (importer, mode, plan) => {
    const modes = importer.modes || [];
    const selected = modes.find(m => m.id === mode) || modes[0];
    if (selected && selected.danger) return `${selected.label}: this deletes the existing data in "${importer.table}" before re-uploading.`;
    return (plan && plan.warning && writesOf(plan) > 0) ? plan.warning : null;
};

/**
 * Undo snapshot of the documents the apply is about to change, stored under the
 * id the import log entry will use. A failure here never blocks the apply: the
 * entry is marked `no_snapshot` and the caller reports it.
 */
const saveUndoSnapshot = async ({ importer, plan, mode, fileName, writes, onProgress }) => {
    if (typeof importer.snapshot !== 'function' || writes === 0) return { entryId: null, error: null, chunks: null };
    const entryId = importUndoService.newEntryId();
    try {
        const items = await importer.snapshot(plan, { mode });
        const saved = await importUndoService.saveSnapshot({ entryId, table: importer.table, mode, fileName, items }, onProgress);
        return { entryId, error: null, chunks: (saved && saved.chunks) || [] };
    } catch (err) {
        logger.error(`[importRunner] undo snapshot failed (${importer.id}):`, err);
        await importUndoService.markNoSnapshot({ entryId, table: importer.table, mode, fileName, error: err });
        return { entryId, error: err, chunks: null };
    }
};

/**
 * Fingerprints of the snapshot documents as the apply left them (the undo only
 * touches what is still like that). Returns `{ changed, error }`; `changed` is
 * null when it could not be measured.
 */
const saveAfterState = async (importer, snapshot) => {
    if (!snapshot.entryId || snapshot.error || !snapshot.chunks) return { changed: null, error: null };
    try {
        const { changed } = await importUndoService.saveAfterState(snapshot.entryId, snapshot.chunks);
        return { changed, error: null };
    } catch (err) {
        logger.error(`[importRunner] after-import state failed (${importer.id}):`, err);
        return { changed: null, error: err };
    }
};

/**
 * The error an apply that failed part-way throws: says whether anything was
 * written and whether "Undo last import" can take it back.
 * `written`: true / false / null (unknown). `undoAvailable`: the partial import
 * is in the log with its snapshot, so the card can offer the undo.
 */
export const partialApplyError = (cause, { written, undoAvailable }) => {
    const reason = cause?.message || String(cause || 'Import failed.');
    let text;
    if (written === false) text = `${reason} Nothing was written; run Preview again before retrying.`;
    else if (undoAvailable) text = `${reason} Part of the import was written before it stopped; "Undo last import" takes back what it wrote, or run Preview again to finish it.`;
    else text = `${reason} Part of the import may have been written before it stopped and it cannot be undone automatically; run Preview again to finish it.`;
    const err = new Error(text);
    err.cause = cause;
    err.partial = written !== false;
    err.written = written;
    err.undoAvailable = !!undoAvailable;
    return err;
};

/**
 * Read-only half: parse the chosen files and plan them. A plan with nothing to
 * write is recorded as a check right away (`recordWhenClean`), exactly like the
 * card's Preview button: `recorded` / `recordError` say how that went.
 * Returns `{ plan, rowCount, writes, info, recorded, recordError }`.
 */
export async function previewImport({ importer, mode, filesById, recordWhenClean = true }) {
    const info = fileInfoOf(importer, filesById);
    if (!info.ready) throw new Error('Choose the file(s) to import first.');

    const multiFile = Array.isArray(importer.files) && importer.files.length > 0;
    const rows = multiFile ? await importer.parse(info.files) : await importer.parse(filesById.file);
    const rowCount = rowCountOf(rows);
    if (rowCount === 0) throw new Error(info.chosen.length > 1 ? 'The files have no rows.' : 'The file has no rows.');

    const plan = await importer.plan(rows, { mode });
    const writes = writesOf(plan);

    let recorded = false;
    let recordError = null;
    if (writes === 0 && recordWhenClean) {
        try {
            await recordCheck(importer, plan, { mode, fileName: info.fileName, fileNames: info.fileNames });
            recorded = true;
        } catch (err) {
            logger.error('[importRunner] check record failed:', err);
            recordError = err;
        }
    }
    return { plan, rowCount, writes, info, recorded, recordError };
}

/**
 * Write half: confirmation gate -> undo snapshot -> importer.apply -> import_log
 * (with the snapshot's entry id) -> audit log. A plan with nothing to write only
 * records the check ("Mark as up to date"). `onStart` fires once the gate is
 * passed and the writing really begins (the card switches to its progress bar).
 * Returns `{ plan, counts, entryId, skipped, cancelled, question, snapshotError, logError }`.
 * Throws (see partialApplyError) when the importer's apply fails: a multi-step or
 * batched apply may already have written part of the file, so that part is put in
 * the import log (mode "partial") with its snapshot and stays undoable.
 */
export async function applyImport({ importer, mode, filesById, plan, currentUser, onProgress, onStart, confirm }) {
    if (!plan) throw new Error('Nothing to apply: run the preview first.');
    const info = fileInfoOf(importer, filesById);
    const { fileName, fileNames } = info;
    const writes = writesOf(plan);

    const question = confirmQuestion(importer, mode, plan);
    if (question) {
        const ok = typeof confirm === 'function' ? await confirm(question, { plan, writes, importer }) : false;
        if (!ok) return { plan, counts: null, entryId: null, skipped: true, cancelled: true, question, snapshotError: null, logError: null };
    }

    if (typeof onStart === 'function') onStart();
    const report = (p) => { if (typeof onProgress === 'function') onProgress(p); };
    report({ done: 0, total: writes || 1, label: 'Starting...' });

    const snapshot = await saveUndoSnapshot({ importer, plan, mode, fileName, writes, onProgress: report });

    let counts = plan.counts;
    let afterError = null;
    if (writes > 0) {
        report({ done: 0, total: writes || 1, label: 'Starting...' });
        let applyError = null;
        let result = null;
        try {
            result = await importer.apply(plan, { mode, fileName, fileNames }, report);
            if (!result || result.success === false) applyError = new Error(result?.error || 'Import failed.');
        } catch (err) {
            applyError = err;
        }

        report({ done: writes, total: writes || 1, label: 'Recording the state after the import...' });
        const after = await saveAfterState(importer, snapshot);
        afterError = after.error;

        if (applyError) {
            // ADM-05: a batched apply (450 documents per batch) can fail with part of the file
            // written. Log that part (mode "partial") with its snapshot, so "Undo last import"
            // can take it back.
            const written = after.changed === null ? null : after.changed > 0;
            let undoAvailable = false;
            if (written !== false && snapshot.entryId && !snapshot.error && !after.error) {
                try {
                    await importLogService.record({ table: importer.table, mode: 'partial', fileName, counts: {}, entryId: snapshot.entryId });
                    undoAvailable = true;
                    await importUndoService.pruneOthers(importer.table, snapshot.entryId);
                } catch (err) {
                    logger.error('[importRunner] partial import could not be logged:', err);
                }
            }
            adminService.logUserAction('IMPORT_FILE', `Database import FAILED part-way: ${importer.title} (${mode})`, currentUser,
                { table: importer.table, fileName, error: applyError?.message || String(applyError), written, undoAvailable });
            throw partialApplyError(applyError, { written, undoAvailable });
        }

        counts = result.stats || plan.counts;
    }

    // Nothing to write ("Mark as up to date" fallback): the file was checked against the
    // database, so the table counts as updated now - record it so the freshness badge turns green.
    let logError = null;
    try {
        if (writes > 0) {
            await importLogService.record({ table: importer.table, mode, fileName, counts, entryId: snapshot.entryId });
            // the log now names this import: the previous snapshot of the table can go
            if (snapshot.entryId && !snapshot.error) await importUndoService.pruneOthers(importer.table, snapshot.entryId);
        } else {
            await recordCheck(importer, plan, { mode, fileName, fileNames });
        }
    } catch (err) {
        logger.error('[importRunner] import_log write failed:', err);
        logError = err;
    }
    adminService.logUserAction('IMPORT_FILE', `Database import: ${importer.title} (${mode})`, currentUser, { table: importer.table, fileName, counts });

    // A snapshot whose after-import state could not be stored is refused by the undo guard:
    // the card reports it exactly like a snapshot that could not be written.
    return { plan, counts, entryId: snapshot.entryId, skipped: writes === 0, cancelled: false, snapshotError: snapshot.error || afterError, logError };
}

/**
 * Which "Undo last import" an importer has: 'store' when it answers its own undo on the engine's store
 * (`storeUndo: { last, run }` - the staff import under USE_CONTRACTS_V2: the engine sends back each document as it
 * was before the import, kept in this browser session only), 'snapshot' when it keeps a Firestore import_undo
 * snapshot (`snapshot()`), else null (no undo for that table).
 */
export const undoKindOf = (importer) => {
    const store = importer?.storeUndo;
    if (store && typeof store.last === 'function' && typeof store.run === 'function') return 'store';
    return typeof importer?.snapshot === 'function' ? 'snapshot' : null;
};

/**
 * The store's last import of this importer as the card's undo entry - `{ store: true, at, mode, docs, restores,
 * creates }`, the counts `undoCounts` gives for a Firestore entry - or null when there is none to undo.
 */
export const storeUndoEntry = (importer) => {
    const last = undoKindOf(importer) === 'store' ? importer.storeUndo.last() : null;
    if (!last) return null;
    const restores = Math.max(0, Number(last.restores || 0));
    const creates = Math.max(0, Number(last.deletes || 0));
    return { store: true, at: last.at || null, mode: last.source || null, fileName: null, docs: restores + creates, restores, creates };
};

/**
 * Takes the store's last import back (`storeUndo.run`: one transaction on the engine; a document changed since the
 * import is kept), then does what the Firestore undo does after it: the table's import log gets an `undo` entry
 * (the freshness badge and the history show it).
 * Returns the Firestore undo's shape, `{ success, restored, deleted, skipped, skippedDocs, logError }`, or
 * `{ success: false, error }` (nothing was undone; the undo stays on offer).
 */
export async function runStoreUndo(importer, onProgress) {
    const res = await importer.storeUndo.run(onProgress);
    if (!res || res.success === false) return { success: false, error: res?.error || 'Undo failed.' };
    const kept = Array.isArray(res.skipped) ? res.skipped : [];
    const out = {
        success: true, restored: Number(res.restored || 0), deleted: Number(res.deleted || 0), skipped: kept.length,
        skippedDocs: kept.map(id => ({ collection: importer.table, id })), logError: null
    };
    // the store is already undone: a log that cannot be written is said, never an "Undo failed"
    try {
        await importLogService.record({
            table: importer.table, mode: 'undo', fileName: null,
            counts: { restored: out.restored, deleted: out.deleted, keptChanged: out.skipped }
        });
    } catch (err) {
        logger.warn(`[importRunner] undo log failed (${importer.id}):`, err);
        out.logError = err;
    }
    return out;
}
